import {
    all,
    call,
    cancel,
    cancelled,
    delay,
    fork,
    put,
    select,
    take,
    takeEvery,
    takeLeading,
} from 'redux-saga/effects';

import { selectors as userSelectors, types as userTypes } from '../user';
import { selectors as wishlistSelectors, types as wishlistTypes } from '../wishlist';
import { selectors as cartSelectors, types as cartTypes } from '../cart';
import { selectors as orderSelectors, types as orderTypes } from '../order';
import { types as searchTypes } from '../search';
import { types as globalTypes } from '../global';
import authCookiesManager from '../../utils/authCookiesManager';

import api from './api';
import { getSmartContent, getSmartRecommendations, isDoNotTrack } from './helpers';

import { actions, selectors, types } from '.';

/**
 * Watches for all actions for setting data into the store required for peerius Tracking + Recommendation.
 * When all necessary data is loaded, sends tracking to Peerius and sets recommendations to the store
 */
export function* watchRequiredDataForTheFirstPeeriusRequest() {
    yield takeLeading(
        [
            types.SET_PEERIUS_DATA,
            userTypes.SET_PROFILE,
            wishlistTypes.SET_WISHLIST,
            orderTypes.SET_ORDER_DATA,
            cartTypes.SET_CART_DATA,
        ],
        sendTrackingGetRecommendations,
    );
}

export function* sendTrackingGetRecommendations() {
    const pageType = yield select(selectors.getPageType);
    if (!pageType) {
        return;
    }
    const track = yield call(collectFullTrackingData, pageType);
    if (!track) {
        return;
    }

    if (isDoNotTrack()) {
        yield put(actions.setRecommendationsByWidgets([]));
        yield put(actions.setSmartContent({ creatives: [] }));
    } else {
        const trackerUrl = yield select(selectors.getTrackerUrl);
        yield call(api.setPeeriusCallbacks, track);
        yield fork(waitForSmartRecs);
        yield call(api.loadPeeriusScript, trackerUrl);
    }
}

export function* waitForSmartRecs() {
    const smartContentTask = yield fork(waitForSmartContent);

    const smartRecs = yield call(getSmartRecommendations);
    yield put(actions.setRecommendationsByWidgets(smartRecs));

    yield delay(300);
    yield cancel(smartContentTask);
}

export function* waitForSmartContent() {
    try {
        const smartContent = yield call(getSmartContent);
        yield put(actions.setSmartContent(smartContent));
    } finally {
        if (yield cancelled()) {
            yield put(actions.setSmartContent({ creatives: [] }));
        }
    }
}

/**
 * Returns an object with the common tracking data required for Tracking + Recommendations requests
 * or false if the data is not ready yet.
 * The Data format is shown here:
 * https://world.episerver.com/documentation/developer-guides/personalization/integration-guides/javascript-api/json-tracking/json-variable-tracking-format
 * @param type according to this flag different fields are added to tracking
 * @returns {(Object|undefined)} undefined if required data isn't fetched yet
 */
export function* collectFullTrackingData(type) {
    const site = yield select(selectors.getClientName);
    const lang = yield select(selectors.getLanguage);
    const isAnonymous = authCookiesManager.isAnonymous();
    const track = {
        site,
        type,
        lang,
    };
    if (!isAnonymous) {
        const customerId = yield select(userSelectors.getCustomerId);
        if (customerId) {
            track.user = { id: customerId };
        } else {
            return;
        }
    } else {
        track.user = { id: '' };
    }
    const pageSpecificTrackingInformation = yield call(
        collectPageSpecificTrackingInformation,
        type,
    );
    if (pageSpecificTrackingInformation) {
        return { ...track, ...pageSpecificTrackingInformation };
    }
}

/**
 * Returns page specific tracking data for ajax tracking requests
 * @param type according to this flag different fields are added to tracking
 * @returns {(Object|undefined)} undefined if required data isn't fetched yet
 */
function* collectPageSpecificTrackingInformation(type) {
    const track = {};
    switch (type) {
        case 'home':
        case 'other':
            // nothing to do
            break;
        case 'product': {
            const refCode = yield select(selectors.getProductCode);
            if (refCode === undefined) {
                return;
            }
            track.product = { refCode };
            break;
        }
        case 'category': {
            const breadcrumbs = yield select(selectors.getBreadcrumbs);
            track.category = breadcrumbs.join('>');
            break;
        }
        case 'searchresults': {
            const term = yield select(selectors.getSearchTerm);
            const totalNumberOfResults = yield select(selectors.getSearchTotalNumber);
            const pageNumber = yield select(selectors.getSearchPageNumber);
            const resultsOnPage = yield select(selectors.getSearchOnPageNumber);
            const filters = yield select(selectors.getSearchFilters);
            const topResultsCodes = yield select(selectors.getSearchTopResultsCodes);
            track.searchResults = {
                term,
                totalNumberOfResults,
                pageNumber,
                resultsOnPage,
                topResults: topResultsCodes.map(refCode => ({ refCode })),
            };
            if (filters.length) {
                track.searchResults.filters = filters;
            }
            break;
        }
        case 'brand':
            track.brand = yield select(selectors.getBrand);
            break;
        case 'wishlist': {
            const isLoaded = yield select(wishlistSelectors.getWishlistCode);
            if (!isLoaded) {
                return;
            }
            const productCodes = yield select(wishlistSelectors.getProductCodes);
            track.wishList = {
                items: productCodes.map(refCode => ({ refCode })),
            };
            break;
        }
        case 'basket':
        case 'checkout': {
            const isLoaded = yield select(cartSelectors.isLoaded);
            if (!isLoaded) {
                return;
            }
            const entries = yield select(cartSelectors.getEntries);
            track[type] = {
                items: fromAbstractOrderEntriesToPeeriusItems(entries),
            };
            const currency = yield select(cartSelectors.getCurrencyIso);
            if (currency) {
                track[type].currency = currency;
            }
            if (type === 'checkout') {
                const cartData = yield select(cartSelectors.getData);
                if (
                    cartData.totalPriceWithoutPromotions &&
                    cartData.totalPriceWithoutPromotions.value
                ) {
                    track[type].subtotal =
                        cartData.totalPriceWithoutPromotions &&
                        cartData.totalPriceWithoutPromotions.value;
                } else {
                    track[type].subtotal = cartData.subtotal && cartData.subtotal.value;
                }
                track[type].shipping = cartData.deliveryCost && cartData.deliveryCost.value;
                track[type].total = cartData.amountToPay && cartData.amountToPay.value;
            }
            break;
        }
        case 'order': {
            const isLoaded = yield select(orderSelectors.isLoaded);
            if (!isLoaded) {
                return;
            }
            const entries = yield select(orderSelectors.getEntries);
            track[type] = {
                items: fromAbstractOrderEntriesToPeeriusItems(entries),
            };

            track[type].currency = yield select(orderSelectors.getSubTotalCurrency);
            track[type].subtotal = yield select(orderSelectors.getSubTotal);
            track[type].shipping = yield select(orderSelectors.getDeliveryCost);
            track[type].total = yield select(orderSelectors.getTotalPrice);
            track[type].orderNo = yield select(orderSelectors.getOrderCode);
            break;
        }
        default:
            track.error = 'Not matched page type';
    }
    return track;
}

function fromAbstractOrderEntriesToPeeriusItems(entries) {
    return entries.map(
        ({
            product: { code: refCode },
            quantity: qty,
            basePrice: { value: basePrice },
            basePriceWithDiscount: { value: basePriceWithDiscount } = {},
        }) => ({
            refCode,
            qty,
            price: basePriceWithDiscount || basePrice,
        }),
    );
}

function cleanUpDynamicPeeriusScripts() {
    [...document.querySelectorAll('[src*=tracker]')].forEach(s => s.parentElement.removeChild(s));
}

/**
 * Performs a peerius flow for a page. Waits for new page visit,
 * cancels previous AJAX watchers and starts again
 */
function* doPeerius() {
    while (true) {
        const pageFlowTask = yield fork(doPageFlow);
        yield take(globalTypes.VISITED);
        yield put(actions.clearPeeriusData());
        yield cancel(pageFlowTask);
        cleanUpDynamicPeeriusScripts();
    }
}

/**
 * Waits until all the data required for peerius tracking is loaded into the store
 * and sends a request to peerius to get recommendations.
 * Only after the first request forks and array of AJAX watchers
 * @returns Array of generators
 */
export function* doPageFlow() {
    const loadFirstPeeriusRequestTask = yield fork(watchRequiredDataForTheFirstPeeriusRequest);
    yield all([take(types.SET_RECOMMENDATIONS_BY_WIDGETS), take(types.SET_SMART_CONTENT)]);
    yield cancel([loadFirstPeeriusRequestTask]);
    const doNotTrack = yield call(isDoNotTrack);
    if (!doNotTrack) {
        yield all(
            [
                watchCartAjaxUpdates,
                watchSendImpressions,
                watchSendClick,
                watchSendContentClick,
                watchFiltersAjaxUpdate,
            ].map(fork),
        );
    }
}

function* watchCartAjaxUpdates() {
    yield takeEvery(
        [cartTypes.ADD_TO_CART, cartTypes.UPDATE_CART, cartTypes.REMOVE_FROM_CART],
        doSendCartTrackingUpdate,
    );
}

function* doSendCartTrackingUpdate() {
    yield take(cartTypes.SET_CART_DATA);
    const peeriusPageType = 'basket';
    const clientName = yield select(selectors.getClientName);
    const track = yield call(collectPageSpecificTrackingInformation, peeriusPageType);
    yield call(api.sendCartTracking, clientName, peeriusPageType, track);
}

function* watchSendImpressions() {
    yield takeEvery(types.SEND_IMPRESSIONS, function*({ payload }) {
        const clientName = yield select(selectors.getClientName);
        yield call(api.sendImpressionsForCarousel, clientName, payload);
    });
}

function* watchSendClick() {
    yield takeEvery(types.SEND_CLICK, function*({ payload: { id, takesToANewPage } }) {
        yield call(api.sendClick, id, takesToANewPage);
    });
}

function* watchSendContentClick() {
    yield takeEvery(types.SEND_CONTENT_CLICK, function*({ payload: { id } }) {
        yield call(api.sendContentClick, id);
    });
}

function* watchFiltersAjaxUpdate() {
    yield takeEvery(searchTypes.CHANGE_FILTER, function*() {
        yield take(types.SET_PEERIUS_DATA);
        yield call(sendTrackingGetRecommendations);
    });
}

export default doPeerius();
